import React, { useState, useMemo, useCallback } from "react";

import {
  Button,
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Row,
  Card,
  Col,
  CardBody,
  CardHeader,
} from "reactstrap";

// RangeSlider
import DeleteModal from "../../../Components/Common/DeleteModal";
import BulkActionBar from "Components/Common/BulkActionBar";
import { clearTableCheckboxes } from "common/lib/tableSelection";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";

import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import AddCapacityAssessment from "./AddCapacityAssessment";
import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
import {
  deleteCapacityAssessment,
  getCapacityAssessments,
} from "common/services/capacity-assessments.service";
import { normalize } from "common/lib/normalize";
import UpdateCapacityAssessment from "./UpdateCapacityAssessment";
import FileIcon from "Components/Common/FileIcon";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import SelectTypeFilter from "./SelectTypeFilter";
import Import from "./Import";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import {
  FilterDateRange,
  FilterLabel,
  FilterSearchBox,
} from "Components/Common/TableFilters";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  dateRange: DateRange;
  capacityAssessmentType: any;
}

const CapacityAssessments = (props: any) => {
  const location = useLocation();
  usePageActivity(location.pathname, "Capacity Assessments");

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const fetchData = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (record: any) => {
    setSelectedRecord(record);
    setIsUpdateModalOpen(true);
  };

  const [deleteModal, setDeleteModal] = useState(false);

  const defaultFilterValue: FilterValue = {
    capacityAssessmentType: null,
    dateRange: {
      fromDate: null,
      toDate: null,
    },
  };
  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );
  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [apiParams, setApiParams] = useState({});

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: FilterValue;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.capacityAssessmentType?.value) {
        andConditions.push({
          typeOfAssessment: {
            id: filterValue?.capacityAssessmentType?.value,
          },
        });
      }

      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(
          filterValue.dateRange.fromDate
        ).toLocaleDateString("en-CA");
        const toDate = new Date(
          filterValue.dateRange.toDate
        ).toLocaleDateString("en-CA");
        andConditions.push({
          reportDate: {
            $between: [fromDate, toDate],
          },
        });
      }

      andConditions.push({
        $or: [
          {
            reportTitle: {
              $containsi: searchValue,
            },
          },
          {
            contractor: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: {
            page,
            pageSize: pagination?.pageSize,
          },
          sort: sortObj,
          populate: [
            "contractor",
            "contractor.organizationType",
            "agency",
            "overallRating",
            "typeOfAssessment",
            "conductedBy",
            "reportFile",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getCapacityAssessments(apiParams);
        setData(normalize(response.data));
        setMeta(response.meta);
      } catch (err: any) {
        toast.error("Error while fetching data", {
          autoClose: 1000,
        });
      } finally {
        setLoading(false);
      }
    },
    [pagination?.pageSize, setSearchParams, unCheckAll]
  );

  const onClickDelete = useCallback(
    (riskAssessment: any) => {
      setSelectedRecord(riskAssessment);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteCapacityAssessment(selectedRecord);
        fetchUpdatedData();
        setDeleteModal(false);
        toast.success("Capacity assessment deleted successfully", {});
      } catch (err: any) {
        toast.error("Error while deleting capacity assessment");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteCapacityAssessment({ id });
          })
        );
        setDeleting(false);
        toast.success("Assessments deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "capacity-assessment",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Report Title",
        accessorKey: "reportTitle",
        id: "reportTitle",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  whiteSpace: "pre-wrap",
                  width: "550px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm rounded">
                    <div className="avatar-title border bg-light text-danger rounded fs-24">
                      <FileIcon
                        fileName={
                          rowData?.reportFile &&
                          Array.isArray(rowData?.reportFile) &&
                          rowData?.reportFile[0]?.name
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1">
                    <Permission
                      alternative={
                        <Link to={"#"} className="text-body">
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="capacity-assessment"
                    >
                      <Link
                        to={`/assessments/capacity-assessments/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    Contractor:
                    <span className="fw-medium ms-1">
                      {cell.row.original?.contractor?.contractorName}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },
      {
        header: "Type of Assessment",
        accessorKey: "typeOfAssessment.name",
        id: "typeOfAssessment.name",
        enableColumnFilter: false,
      },
      {
        header: "Overall Rating",
        accessorKey: "overallRating.name",
        id: "overallRating.name",
        enableColumnFilter: false,
      },

      {
        header: "UN Agency",
        accessorKey: "agency.name",
        id: "agency.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
              }}
              className="text-truncate d-block"
            >
              {cell.row.original?.agency?.name || ""}
            </Link>
          );
        },
      },
      {
        header: "Assessment Date",
        accessorKey: "reportDate",
        id: "reportDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
      {
        header: "Action",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission action="findOne" resource="capacity-assessment">
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/assessments/capacity-assessments/${rowData.id}`
                      );
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>
                <Permission action="update" resource="capacity-assessment">
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm(rowData);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission action="delete" resource="capacity-assessment">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );
  document.title = "Capacity Assessments | CIMS";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Capacity / Micro Assessments"
          pageTitles={[
            { title: "Dashboard", link: "/dashboard" },
            {
              title: "Assessments",
              link: "/assessments/capacity-assessments",
            },
          ]}
        />
        {/* <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="border-0">
                <div className="d-flex align-items-center">
                  <h5 className="card-title mb-0 flex-grow-1">
                    Explore Product
                  </h5>
                  <div>
                    <Link
                      className="btn btn-success"
                      id="filter-collapse"
                      data-bs-toggle="collapse"
                      to="#collapseExample"
                    >
                      <i className="ri-filter-2-line align-bottom"></i>
                      Filters
                    </Link>
                  </div>
                </div>

                <UncontrolledCollapse toggler="#filter-collapse" defaultOpen>
                  <Row className="row-cols-xxl-5 row-cols-lg-3 row-cols-md-2 row-cols-1 mt-3 g-3">
                    <Col>
                      <h6 className="text-uppercase fs-12 mb-2">Search</h6>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search product name"
                        autoComplete="off"
                        id="searchProductList"
                        // onKeyUp={searchNFT}
                      />
                    </Col>
                    <Col>
                      <h6 className="text-uppercase fs-12 mb-2">
                        Select Category
                      </h6>
                      <select
                        className="form-control"
                        data-choices
                        name="select-category"
                        data-choices-search-false
                        id="select-category"
                        // onChange={(e) => category(e.target.value)}
                      >
                        <option value="">Select Category</option>
                        <option value="Artwork">Artwork</option>
                        <option value="3d Style">3d Style</option>
                        <option value="Photography">Photography</option>
                        <option value="Collectibles">Collectibles</option>
                        <option value="Crypto Card">Crypto Card</option>
                        <option value="Games">Games</option>
                        <option value="Music">Music</option>
                      </select>
                    </Col>
                    <Col>
                      <h6 className="text-uppercase fs-12 mb-2">File Type</h6>
                      <select
                        className="form-control"
                        data-choices
                        name="file-type"
                        data-choices-search-false
                        id="file-type"
                        // onChange={(e) => fileType(e.target.value)}
                      >
                        <option value="">File Type</option>
                        <option value="jpg">Images</option>
                        <option value="mp4">Video</option>
                        <option value="mp3">Audio</option>
                        <option value="gif">Gif</option>
                      </select>
                    </Col>
                    <Col>
                      <h6 className="text-uppercase fs-12 mb-2">Sales Type</h6>
                      <select
                        className="form-control"
                        data-choices
                        name="all-sales-type"
                        data-choices-search-false
                        id="all-sales-type"
                        // onChange={(e) => salesType(e.target.value)}
                      >
                        <option value="">All Sales Type</option>
                        <option value="On Auction">On Auction</option>
                        <option value="Has Offers">Has Offers</option>
                      </select>
                    </Col>
                    <Col>
                      <h6 className="text-uppercase fs-12 mb-4">Price</h6>
                    </Col>
                  </Row>
                </UncontrolledCollapse>
              </CardHeader>
            </Card>
          </Col>
          <Col lg={12}>
            <div className="d-flex align-items-center mb-4">
              <div className="flex-grow-1">
                <p className="text-muted fs-14 mb-0">Result: 8745</p>
              </div>
              <div className="flex-shrink-0">
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="a"
                    className="text-muted fs-14"
                    role="button"
                  >
                    All View <i className="mdi mdi-chevron-down"></i>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem to="#">Action</DropdownItem>
                    <DropdownItem to="#">Another action</DropdownItem>
                    <DropdownItem to="#">Something else here</DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          </Col>
        </Row> */}
        <Row>
          <Col xl={12} lg={12}>
            <Card>
              <CardHeader className="">
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="card-title mb-1">
                      Capacity / Micro Assessments
                    </h5>
                    <p className="text-muted mb-0">
                      This page shows the HACT micro-assessments, SHF
                      assessments that have been provided by the UN Agencies.
                      Use the search tool below to find a specific assessment.
                    </p>
                  </Col>
                  <div className="col-md-auto ms-auto">
                    <div className="d-flex gap-2">
                      <Permission
                        resource="capacity-assessment"
                        action="create"
                      >
                        <Link
                          to="/#"
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            showAddModalForm();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          Add Assessment
                        </Link>
                      </Permission>
                      <Permissions
                        checks={[
                          {
                            resource: "capacity-assessment",
                            action: "import",
                          },
                          {
                            resource: "capacity-assessment",
                            action: "export",
                          },
                        ]}
                      >
                        <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                      </Permissions>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col lg={5} md={12}>
                    <FilterSearchBox
                      id="capacity-assessment-search"
                      placeholder="Search capacity assessments"
                      value={searchValue}
                      onChange={(value: string) => {
                        setSearchValue(value);
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col lg={3} md={6}>
                    <FilterLabel htmlFor="capacityAssessmentType">
                      Type
                    </FilterLabel>
                    <SelectTypeFilter
                      filterValue={filterValue}
                      setFilterValue={(value: any) => {
                        setFilterValue({
                          ...filterValue,
                          capacityAssessmentType: value,
                        });
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <FilterDateRange
                      id="capacity-assessment-date-range"
                      value={filterValue?.dateRange}
                      onChange={(dateRange: any) => {
                        setFilterValue({ ...filterValue, dateRange });
                        setPage(1);
                      }}
                    />
                  </Col>

                </Row>
              </CardBody>
            </Card>
            <Card className="">
              <CardBody>
                <TableContainer
                  loading={loading}
                  columns={columns}
                  data={data || []}
                  pagination={pagination}
                  setPagination={setPagination}
                  sorted={sorted}
                  setSorted={setSorted}
                  onPageChange={onPageChange}
                  FilterSection={() => { }}
                  totalRows={meta?.pagination?.total || 0}
                  totalPages={meta?.pagination?.pageCount || 0}
                  searchValue={searchValue}
                  filterValue={filterValue}
                  columnVisibility={columnVisibility}
                  divClass="table-responsive table-card mb-4"
                  tableClass="align-middle table-nowrap mb-0 table-striped "
                  theadClass=" table-nowrap "
                  thClass={"pt-3"}
                />
                <AddCapacityAssessment
                  toggle={() => setIsAddModalOpen((state) => !state)}
                  isModalOpen={isAddModalOpen}
                  fetchData={fetchData}
                />
                <UpdateCapacityAssessment
                  toggle={() => setIsUpdateModalOpen((state) => !state)}
                  isModalOpen={isUpdateModalOpen}
                  selectedRecord={selectedRecord}
                  fetchUpdatedData={fetchUpdatedData}
                />
                <DeleteModal
                  show={deleteModal}
                  onDeleteClick={() => handleDelete()}
                  onCloseClick={() => setDeleteModal(false)}
                  loading={deleting}
                />
                <DeleteModal
                  show={deleteMultipleModal}
                  onDeleteClick={() => handleDeleteMultiple()}
                  onCloseClick={() => setDeleteMultipleModal(false)}
                  loading={deleting}
                />
                <Permissions
                  checks={[{ resource: "capacity-assessment", action: "delete" }]}
                >
                  <BulkActionBar
                    count={selectedCheckBox.length}
                    onClear={unCheckAll}
                    itemLabel="capacity assessment"
                  >
                    <Button
                      color="danger"
                      size="sm"
                      className="material-shadow-none"
                      onClick={() => setDeleteMultipleModal(true)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                      Delete
                    </Button>
                  </BulkActionBar>
                </Permissions>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CapacityAssessments;
